import { doc, getDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import {
  ALLOWED_STATUSES,
  HEADER_ALIASES,
  LEDEN_HEADERS,
  MAX_ROWS_PER_UPLOAD,
  MAX_UPLOAD_FILE_BYTES,
  deriveLidAantalFromStatus
} from '../config/ledenUploadTemplate';
import { createWorkbook, downloadWorkbook, readWorkbookFromFile } from './spreadsheet';
import { APP_ERROR_CODES, createAppError } from './appErrors';

const CONVIVENTIES_COLLECTION = 'conviventies';
const GEMEENSCHAPPEN_UPLOADS_SUBCOLLECTION = 'ledenUploadsGemeenschappen';
const GASTENGROEPEN_UPLOADS_SUBCOLLECTION = 'ledenUploadsGastengroepen';

const HEADER_INDEX = LEDEN_HEADERS.reduce((acc, header) => {
  acc[normalizeHeader(header)] = header;
  return acc;
}, {});

Object.entries(HEADER_ALIASES).forEach(([alias, target]) => {
  HEADER_INDEX[normalizeHeader(alias)] = target;
});

const STATUS_INDEX = ALLOWED_STATUSES.reduce((acc, status) => {
  acc[normalizeValue(status)] = status;
  return acc;
}, {});

function normalizeHeader(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeValue(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeTargetType(targetType) {
  const value = normalizeValue(targetType);
  if (value === 'guestgroup' || value === 'gastengroep') return 'guestGroup';
  return 'gemeenschap';
}

function getUploadsSubcollection(targetType) {
  return normalizeTargetType(targetType) === 'guestGroup'
    ? GASTENGROEPEN_UPLOADS_SUBCOLLECTION
    : GEMEENSCHAPPEN_UPLOADS_SUBCOLLECTION;
}

function getUploadDocRef(conviventieId, targetId, targetType) {
  return doc(
    db,
    CONVIVENTIES_COLLECTION,
    conviventieId,
    getUploadsSubcollection(targetType),
    targetId
  );
}

function getConviventieDocRef(conviventieId) {
  return doc(db, CONVIVENTIES_COLLECTION, conviventieId);
}

function getUserEmail() {
  return auth.currentUser?.email || 'unknown';
}

function formatYmd(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function asDateFromExcelSerial(serial) {
  if (typeof serial !== 'number' || Number.isNaN(serial)) return null;

  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400;
  const dateInfo = new Date(utcValue * 1000);

  if (Number.isNaN(dateInfo.getTime())) return null;
  return new Date(dateInfo.getFullYear(), dateInfo.getMonth(), dateInfo.getDate());
}

function normalizeBirthDate(value) {
  if (!value && value !== 0) return '';

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return formatYmd(value);
  }

  if (typeof value === 'number') {
    const parsedDate = asDateFromExcelSerial(value);
    return parsedDate ? formatYmd(parsedDate) : null;
  }

  const text = String(value).trim();
  if (!text) return '';

  const ymdMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdMatch) {
    const parsed = new Date(`${ymdMatch[1]}-${ymdMatch[2]}-${ymdMatch[3]}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : formatYmd(parsed);
  }

  const dmyMatch = text.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!dmyMatch) return null;

  const day = Number(dmyMatch[1]);
  const month = Number(dmyMatch[2]);
  const year = Number(dmyMatch[3]);
  const parsedDate = new Date(year, month - 1, day);

  if (
    Number.isNaN(parsedDate.getTime()) ||
    parsedDate.getFullYear() !== year ||
    parsedDate.getMonth() + 1 !== month ||
    parsedDate.getDate() !== day
  ) {
    return null;
  }

  return formatYmd(parsedDate);
}

function isValidEmail(value) {
  if (!value) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function slugify(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function buildStableRowId(row) {
  const stem = slugify(`${row.achternaam}|${row.voornaam}|${row.geboortedatum || ''}`);
  return stem || 'lid';
}

function toStringValue(value) {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value;
  return String(value).trim();
}

function getRowCellValue(row, cellIndex) {
  if (!row) return '';
  if (typeof row.getCell === 'function') {
    return row.getCell(cellIndex)?.value;
  }
  const values = row.values || [];
  return values[cellIndex];
}

function resolveHeaderMap(worksheet) {
  const headerRow = worksheet.getRow(1);
  const headerMap = {};

  for (let columnIndex = 1; columnIndex <= (headerRow.cellCount || LEDEN_HEADERS.length + 2); columnIndex += 1) {
    const rawHeader = toStringValue(getRowCellValue(headerRow, columnIndex));
    if (!rawHeader || rawHeader instanceof Date) continue;

    const normalized = normalizeHeader(rawHeader);
    const canonical = HEADER_INDEX[normalized];

    if (canonical && !headerMap[canonical]) {
      headerMap[canonical] = columnIndex;
    }
  }

  return headerMap;
}

function extractCanonicalRow(worksheet, rowIndex, headerMap) {
  const row = worksheet.getRow(rowIndex);
  const data = {};

  LEDEN_HEADERS.forEach((header) => {
    const cellIndex = headerMap[header];
    data[header] = cellIndex ? getRowCellValue(row, cellIndex) : '';
  });

  return data;
}

function isCanonicalRowEmpty(row) {
  return LEDEN_HEADERS.every((header) => {
    const value = toStringValue(row[header]);
    if (value instanceof Date) return false;
    return String(value).trim() === '';
  });
}

const normalizeLidKey = (lid) => String(lid?.lidId || lid?.id || '').trim();

/**
 * Re-maps an existing confirmation onto a changed ledenlijst (Feature 35).
 *
 * A manual member edit must not reset the whole confirmation of a gemeenschap:
 * - members still in the list keep their aankomst/vertrek (and confirmation notes),
 * - members removed from the list lose their confirmation,
 * - newly added members are not confirmed.
 *
 * The returned records use the same shape `confirmLeden` writes (see
 * `conviventieService.js`), so the confirmation panel, the Excel export and the
 * confirmed counts stay consistent.
 */
const reconcileConfirmedLeden = (previousConfirmedLeden, leden) => {
  const previousById = new Map();

  (Array.isArray(previousConfirmedLeden) ? previousConfirmedLeden : []).forEach((lid) => {
    const key = normalizeLidKey(lid);
    if (key) previousById.set(key, lid);
  });

  const reconciled = [];

  (Array.isArray(leden) ? leden : []).forEach((lid) => {
    const id = normalizeLidKey(lid);
    const previous = id ? previousById.get(id) : null;

    if (!previous) return;

    const status = String(lid.status || '').trim();

    reconciled.push({
      lidId: previous.lidId || id,
      voornaam: lid.voornaam || '',
      achternaam: lid.achternaam || '',
      status,
      aantal: deriveLidAantalFromStatus(status),
      geboortedatum: lid.geboortedatum || '',
      email: lid.email || '',
      telefoon: lid.telefoon || '',
      confirmed: previous.confirmed !== false,
      bijzonderheden: previous.bijzonderheden || lid.bijzonderheden || '',
      aankomst: previous.aankomst || '',
      vertrek: previous.vertrek || ''
    });
  });

  return reconciled;
};

/**
 * Builds the conviventie write that flips the uploadStatus of one invited target.
 *
 * The conviventie document is read here — never inside a write — so the caller can
 * commit this update together with the upload document in one atomic batch
 * (Feature 30): either both documents land, or neither does.
 *
 * @param {boolean|'auto'} options.touchUploadedAt - `true` always refreshes
 *   uploadedAt/uploadedBy, `false` never does, `'auto'` only refreshes them on a
 *   pending → uploaded transition (used by member edits, which must not rewrite
 *   the original upload metadata).
 * @param {Array|null} options.reconcileLeden - When provided, the update is
 *   non-destructive (Feature 35): an existing confirmation is kept (the status
 *   stays `confirmed`) and re-mapped onto this ledenlijst, so confirmed members
 *   keep their aankomst/vertrek and notes while removed members lose theirs.
 * @param {boolean} options.tolerateMissingTarget - When the target is no longer
 *   invited (removed by an admin in the meantime), return `null` instead of
 *   throwing, so the caller can still erase the orphaned payload. Used by
 *   `deleteUploadedLeden()`, which also runs as best-effort cleanup for
 *   de-invited targets whose entry `updateConviventie()` already removed.
 * @returns {Promise<{ref: object, data: object}|null>} the conviventie update, or
 *   `null` when the target is missing and `tolerateMissingTarget` was set.
 */
async function buildUploadStatusUpdate(
  conviventieId,
  targetId,
  targetType,
  userEmail,
  status,
  { touchUploadedAt = true, reconcileLeden = null, tolerateMissingTarget = false } = {}
) {
  const conviventieRef = getConviventieDocRef(conviventieId);
  const conviventieSnapshot = await getDoc(conviventieRef);

  if (!conviventieSnapshot.exists()) {
    throw createAppError(APP_ERROR_CODES.CONVIVENTIE_MISSING, 'Conviventie niet gevonden');
  }

  const conviventie = conviventieSnapshot.data();
  const isGuestGroup = normalizeTargetType(targetType) === 'guestGroup';
  const fieldName = isGuestGroup ? 'invitedGuestGroups' : 'uitgenodigdeGemeenschappen';
  const idKey = isGuestGroup ? 'guestGroupId' : 'gemeenschapId';
  const list = Array.isArray(conviventie[fieldName]) ? conviventie[fieldName] : [];

  let found = false;
  const updatedList = list.map((item) => {
    if (item?.[idKey] !== targetId) return item;

    found = true;
    const wasConfirmed = normalizeValue(item?.uploadStatus) === 'confirmed';
    const wasPending = !item?.uploadStatus || normalizeValue(item?.uploadStatus) === 'pending';
    const shouldTouchUploadedAt = touchUploadedAt === 'auto' ? wasPending : touchUploadedAt;
    const shouldPreserveConfirmation = !!reconcileLeden && wasConfirmed && status !== 'pending';

    return {
      ...item,
      uploadStatus: shouldPreserveConfirmation ? 'confirmed' : status,
      ...(shouldTouchUploadedAt ? { uploadedAt: new Date(), uploadedBy: userEmail } : {}),
      bevestigdeLeden: reconcileLeden
        ? reconcileConfirmedLeden(item?.bevestigdeLeden, reconcileLeden)
        : (status === 'pending' || wasConfirmed ? [] : (item?.bevestigdeLeden || []))
    };
  });

  if (!found) {
    if (tolerateMissingTarget) {
      return null;
    }

    throw createAppError(
      APP_ERROR_CODES.TARGET_MISSING,
      isGuestGroup
        ? 'Gastengroep niet gevonden in uitgenodigde lijst'
        : 'Gemeenschap niet gevonden in uitgenodigde lijst'
    );
  }

  return {
    ref: conviventieRef,
    data: {
      [fieldName]: updatedList,
      updatedAt: serverTimestamp(),
      updatedBy: userEmail
    }
  };
}

export const parseLedenExcel = async (file) => {
  const errors = [];
  const warnings = [];

  if (!file) {
    return {
      leden: [],
      errors: [{ row: null, field: 'bestand', message: 'Geen bestand geselecteerd' }],
      warnings
    };
  }

  const filename = String(file.name || '').toLowerCase();
  if (!/\.xlsx?$/.test(filename)) {
    return {
      leden: [],
      errors: [{ row: null, field: 'bestand', message: 'Alleen .xlsx of .xls bestanden zijn toegestaan' }],
      warnings
    };
  }

  if (typeof file.size === 'number' && file.size > MAX_UPLOAD_FILE_BYTES) {
    return {
      leden: [],
      errors: [{ row: null, field: 'bestand', message: `Bestand is te groot (max ${Math.floor(MAX_UPLOAD_FILE_BYTES / (1024 * 1024))}MB)` }],
      warnings
    };
  }

  const workbook = await readWorkbookFromFile(file);
  const worksheet = workbook?.worksheets?.[0];

  if (!worksheet) {
    return {
      leden: [],
      errors: [{ row: null, field: 'bestand', message: 'Werkblad niet gevonden' }],
      warnings
    };
  }

  const headerMap = resolveHeaderMap(worksheet);
  const missingHeaders = LEDEN_HEADERS.filter((header) => !headerMap[header]);

  if (missingHeaders.length > 0) {
    return {
      leden: [],
      errors: [{ row: 1, field: 'kolommen', message: `Ontbrekende kolommen: ${missingHeaders.join(', ')}` }],
      warnings
    };
  }

  const rawRows = [];
  for (let rowIndex = 2; rowIndex <= (worksheet.rowCount || 0); rowIndex += 1) {
    const canonicalRow = extractCanonicalRow(worksheet, rowIndex, headerMap);
    if (isCanonicalRowEmpty(canonicalRow)) continue;
    rawRows.push({ rowIndex, canonicalRow });
  }

  if (rawRows.length > MAX_ROWS_PER_UPLOAD) {
    return {
      leden: [],
      errors: [{ row: null, field: 'rijen', message: `Te veel rijen: maximaal ${MAX_ROWS_PER_UPLOAD}` }],
      warnings
    };
  }

  const duplicateTracker = new Map();
  const idCounters = new Map();
  const leden = [];

  rawRows.forEach(({ rowIndex, canonicalRow }) => {
    const achternaam = String(toStringValue(canonicalRow.Achternaam)).trim();
    const voornaam = String(toStringValue(canonicalRow.Voornaam)).trim();
    const statusInput = String(toStringValue(canonicalRow.Status)).trim();
    const status = STATUS_INDEX[normalizeValue(statusInput)] || '';
    const geboortedatum = normalizeBirthDate(canonicalRow.Geboortedatum);
    const email = String(toStringValue(canonicalRow.Email)).trim();
    const telefoon = String(toStringValue(canonicalRow.Telefoon)).trim();
    const bijzonderheden = String(toStringValue(canonicalRow.Bijzonderheden)).trim();

    if (!achternaam) {
      errors.push({ row: rowIndex, field: 'Achternaam', message: 'Achternaam is verplicht' });
    }

    if (!voornaam) {
      errors.push({ row: rowIndex, field: 'Voornaam', message: 'Voornaam is verplicht' });
    }

    if (!status) {
      errors.push({
        row: rowIndex,
        field: 'Status',
        message: `Status is ongeldig. Toegestaan: ${ALLOWED_STATUSES.join(', ')}`
      });
    }

    if (geboortedatum === null) {
      errors.push({
        row: rowIndex,
        field: 'Geboortedatum',
        message: 'Geboortedatum moet DD-MM-YYYY of YYYY-MM-DD zijn'
      });
    }

    if (!isValidEmail(email)) {
      errors.push({ row: rowIndex, field: 'Email', message: 'Email is ongeldig' });
    }

    const duplicateKey = `${normalizeValue(achternaam)}|${normalizeValue(voornaam)}`;
    if (achternaam && voornaam) {
      const duplicateCount = (duplicateTracker.get(duplicateKey) || 0) + 1;
      duplicateTracker.set(duplicateKey, duplicateCount);
      if (duplicateCount > 1) {
        warnings.push({
          row: rowIndex,
          field: 'Naam',
          message: `Mogelijk duplicaat: ${voornaam} ${achternaam}`
        });
      }
    }

    const rowPayload = {
      achternaam,
      voornaam,
      status,
      aantal: deriveLidAantalFromStatus(status),
      geboortedatum: geboortedatum || '',
      email,
      telefoon,
      bijzonderheden
    };

    const idStem = buildStableRowId(rowPayload);
    const idOccurrence = (idCounters.get(idStem) || 0) + 1;
    idCounters.set(idStem, idOccurrence);

    leden.push({
      id: idOccurrence === 1 ? idStem : `${idStem}-${idOccurrence}`,
      ...rowPayload
    });
  });

  return { leden, errors, warnings };
};

/**
 * Replaces the complete ledenlijst of a conviventie target from an Excel upload.
 *
 * Deliberately destructive (Feature 35): a completely new file replaces the list
 * and resets a previous confirmation, including aankomst/vertrek dates. Only the
 * manual member edits via the Leden page are non-destructive.
 */
export const uploadLedenForConviventie = async (
  conviventieId,
  targetId,
  targetType,
  leden,
  { expiresAt } = {}
) => {
  const userEmail = getUserEmail();
  const normalizedTargetType = normalizeTargetType(targetType);
  const uploadRef = getUploadDocRef(conviventieId, targetId, normalizedTargetType);
  const conviventieUpdate = await buildUploadStatusUpdate(
    conviventieId,
    targetId,
    normalizedTargetType,
    userEmail,
    'uploaded'
  );

  // One atomic batch (Feature 30): the ledenlijst and the target's status change
  // together, so a failure can never leave an upload document without its status.
  const batch = writeBatch(db);
  batch.set(uploadRef, {
    targetId,
    targetType: normalizedTargetType,
    leden: Array.isArray(leden) ? leden : [],
    validationStatus: 'validated',
    uploadedAt: serverTimestamp(),
    uploadedBy: userEmail,
    expiresAt: expiresAt || null
  });
  batch.update(conviventieUpdate.ref, conviventieUpdate.data);
  await batch.commit();
};

export const getUploadedLeden = async (conviventieId, targetId, targetType) => {
  const uploadRef = getUploadDocRef(conviventieId, targetId, targetType);
  const snapshot = await getDoc(uploadRef);

  if (!snapshot.exists()) {
    return { leden: [] };
  }

  const data = snapshot.data() || {};
  return {
    leden: Array.isArray(data.leden) ? data.leden : [],
    uploadedAt: data.uploadedAt || null,
    uploadedBy: data.uploadedBy || '',
    validationStatus: data.validationStatus || ''
  };
};

/**
 * Erases the ledenlijst of one conviventie target and resets the entry to `pending`.
 *
 * The deletion and the status flip are committed in one atomic batch (Feature 30),
 * so a failure can never leave an orphan upload document behind. A target that is
 * no longer invited (de-invited while a panel was open, or the best-effort cleanup
 * after `updateConviventie()` already removed the entry) only needs its payload
 * erased, so a missing entry is tolerated instead of throwing.
 */
export const deleteUploadedLeden = async (conviventieId, targetId, targetType) => {
  const uploadRef = getUploadDocRef(conviventieId, targetId, targetType);
  const userEmail = getUserEmail();
  const conviventieUpdate = await buildUploadStatusUpdate(
    conviventieId,
    targetId,
    targetType,
    userEmail,
    'pending',
    { tolerateMissingTarget: true }
  );

  const batch = writeBatch(db);
  batch.delete(uploadRef);

  if (conviventieUpdate) {
    batch.update(conviventieUpdate.ref, conviventieUpdate.data);
  }

  await batch.commit();
};

const sanitizeLidRecord = (lid) => {
  const status = String(lid?.status || '').trim();

  return {
    id: lid?.id,
    voornaam: String(lid?.voornaam || '').trim(),
    achternaam: String(lid?.achternaam || '').trim(),
    status,
    aantal: deriveLidAantalFromStatus(status),
    geboortedatum: String(lid?.geboortedatum || '').trim(),
    email: String(lid?.email || '').trim(),
    telefoon: String(lid?.telefoon || '').trim(),
    bijzonderheden: String(lid?.bijzonderheden || '').trim()
  };
};

const buildLidId = (existingLeden, lid) => {
  const stem = buildStableRowId({
    achternaam: lid?.achternaam,
    voornaam: lid?.voornaam,
    geboortedatum: lid?.geboortedatum
  });
  const usedIds = new Set((existingLeden || []).map((item) => item?.id));

  if (!usedIds.has(stem)) return stem;

  let occurrence = 2;
  while (usedIds.has(`${stem}-${occurrence}`)) {
    occurrence += 1;
  }

  return `${stem}-${occurrence}`;
};

async function readUploadDoc(conviventieId, targetId, targetType) {
  const uploadRef = getUploadDocRef(conviventieId, targetId, targetType);
  const snapshot = await getDoc(uploadRef);
  const data = snapshot.exists() ? (snapshot.data() || {}) : {};

  return {
    uploadRef,
    exists: snapshot.exists(),
    leden: Array.isArray(data.leden) ? data.leden : [],
    uploadedAt: data.uploadedAt || null,
    uploadedBy: data.uploadedBy || '',
    expiresAt: data.expiresAt || null
  };
}

/**
 * Persists a full leden array for one conviventie target.
 *
 * - Keeps the original `uploadedAt`/`uploadedBy` (they describe the moment the
 *   ledenlijst was loaded); manual edits only refresh `updatedAt`/`updatedBy`.
 * - Non-destructive by default (Feature 35): a previous confirmation is kept and
 *   re-mapped onto the new list, so already confirmed members keep their
 *   aankomst/vertrek dates and notes. Pass `preserveConfirmation: false` for a
 *   destructive replacement (used by the Excel re-upload).
 * - Flips the target to `uploaded` (or keeps it `confirmed` when the confirmation
 *   is preserved) and stamps `uploadedAt`/`uploadedBy` when the list was still
 *   `pending`.
 * - Writes an empty list by deleting the upload document, resetting the target
 *   to `pending`.
 * - Commits the ledenlijst and the target's status flip in one atomic batch
 *   (Feature 30), so the two can never diverge.
 */
const writeUploadedLeden = async (
  conviventieId,
  targetId,
  targetType,
  leden,
  { expiresAt, preserveConfirmation = true } = {}
) => {
  const normalizedTargetType = normalizeTargetType(targetType);
  const userEmail = getUserEmail();
  const current = await readUploadDoc(conviventieId, targetId, normalizedTargetType);

  if (leden.length === 0) {
    if (current.exists) {
      await deleteUploadedLeden(conviventieId, targetId, normalizedTargetType);
    }
    return [];
  }

  const conviventieUpdate = await buildUploadStatusUpdate(
    conviventieId,
    targetId,
    normalizedTargetType,
    userEmail,
    'uploaded',
    {
      touchUploadedAt: 'auto',
      reconcileLeden: preserveConfirmation ? leden : null
    }
  );

  const batch = writeBatch(db);
  batch.set(current.uploadRef, {
    targetId,
    targetType: normalizedTargetType,
    leden,
    validationStatus: 'validated',
    uploadedAt: current.exists ? current.uploadedAt : serverTimestamp(),
    uploadedBy: current.exists ? current.uploadedBy : userEmail,
    expiresAt: current.expiresAt || expiresAt || null,
    updatedAt: serverTimestamp(),
    updatedBy: userEmail
  });
  batch.update(conviventieUpdate.ref, conviventieUpdate.data);
  await batch.commit();

  return leden;
};

/**
 * Adds a member to a conviventie's ledenlijst and returns the new leden array.
 */
export const addUploadedLid = async (conviventieId, targetId, targetType, lid, options = {}) => {
  const current = await readUploadDoc(conviventieId, targetId, targetType);
  const record = sanitizeLidRecord({
    ...lid,
    id: lid?.id || buildLidId(current.leden, lid)
  });

  return writeUploadedLeden(
    conviventieId,
    targetId,
    targetType,
    [...current.leden, record],
    options
  );
};

/**
 * Updates one member of a conviventie's ledenlijst and returns the leden array.
 */
export const updateUploadedLid = async (conviventieId, targetId, targetType, lidId, updates, options = {}) => {
  const current = await readUploadDoc(conviventieId, targetId, targetType);
  let found = false;

  const leden = current.leden.map((lid) => {
    if (lid?.id !== lidId) return lid;

    found = true;
    return sanitizeLidRecord({ ...lid, ...updates, id: lidId });
  });

  if (!found) {
    throw new Error('Lid niet gevonden in de ledenlijst');
  }

  return writeUploadedLeden(conviventieId, targetId, targetType, leden, options);
};

/**
 * Removes one member from a conviventie's ledenlijst and returns the leden array.
 * Removing the last member deletes the upload document (target back to `pending`).
 */
export const deleteUploadedLid = async (conviventieId, targetId, targetType, lidId) => {
  const current = await readUploadDoc(conviventieId, targetId, targetType);
  const leden = current.leden.filter((lid) => lid?.id !== lidId);

  return writeUploadedLeden(conviventieId, targetId, targetType, leden);
};

/**
 * Maps one stored lid onto a template row, in `LEDEN_HEADERS` order.
 *
 * Values are written as plain strings (never Date objects): the parser accepts
 * the stored `YYYY-MM-DD` form, and avoiding Excel serial/Date conversion keeps
 * the download → edit → re-upload round trip predictable.
 */
const buildTemplateRow = (lid) => {
  const byHeader = {
    Voornaam: lid?.voornaam,
    Achternaam: lid?.achternaam,
    Status: lid?.status,
    Geboortedatum: lid?.geboortedatum,
    Email: lid?.email,
    Telefoon: lid?.telefoon,
    Bijzonderheden: lid?.bijzonderheden
  };

  return LEDEN_HEADERS.map((header) => {
    const value = byHeader[header];
    return value === null || value === undefined ? '' : String(value);
  });
};

/**
 * Writes the ledenlijst Excel template and triggers the browser download.
 *
 * Feature 36: passing `leden` (the currently loaded ledenlijst of one target)
 * pre-fills the sheet, so an existing list can be corrected offline and uploaded
 * back unchanged — `parseLedenExcel` accepts the generated file as-is. The Status
 * dropdown and the Geboortedatum format are applied to every row, prefilled or
 * blank, so any row stays editable.
 *
 * @param {string} [filename] - Download file name.
 * @param {Array} [leden] - Existing leden to pre-fill (default: blank template).
 */
export const downloadLedenTemplate = async (filename = 'leden-template.xlsx', leden = []) => {
  const workbook = createWorkbook();
  const worksheet = workbook.addWorksheet('Ledenlijst');

  worksheet.addRow(LEDEN_HEADERS);
  worksheet.getRow(1).font = { bold: true };
  worksheet.views = [{ state: 'frozen', ySplit: 1 }];

  const prefilledLeden = (Array.isArray(leden) ? leden : []).slice(0, MAX_ROWS_PER_UPLOAD);

  for (let rowIndex = 2; rowIndex <= MAX_ROWS_PER_UPLOAD + 1; rowIndex += 1) {
    const lid = prefilledLeden[rowIndex - 2];
    worksheet.addRow(lid ? buildTemplateRow(lid) : new Array(LEDEN_HEADERS.length).fill(''));
    worksheet.getCell(`C${rowIndex}`).dataValidation = {
      type: 'list',
      allowBlank: false,
      formulae: [`"${ALLOWED_STATUSES.join(',')}"`]
    };
    worksheet.getCell(`D${rowIndex}`).numFmt = 'dd-mm-yyyy';
  }

  worksheet.columns = LEDEN_HEADERS.map((header) => ({
    header,
    key: header,
    width: Math.max(header.length + 4, 18)
  }));

  const instructions = workbook.addWorksheet('Instructies');
  instructions.addRow(['Kolom', 'Regel']);
  instructions.getRow(1).font = { bold: true };
  instructions.addRow(['Voornaam', 'Verplicht']);
  instructions.addRow(['Achternaam', 'Verplicht']);
  instructions.addRow(['Status', `Verplicht (${ALLOWED_STATUSES.join(', ')})`]);
  instructions.addRow(['Geboortedatum', 'Optioneel (DD-MM-YYYY of Excel datum)']);
  instructions.addRow(['Email', 'Optioneel']);
  instructions.addRow(['Telefoon', 'Optioneel']);
  instructions.addRow(['Bijzonderheden', 'Optioneel']);
  instructions.addRow(['Max rijen', String(MAX_ROWS_PER_UPLOAD)]);

  instructions.columns = [
    { header: 'Kolom', key: 'kolom', width: 22 },
    { header: 'Regel', key: 'regel', width: 56 }
  ];

  await downloadWorkbook(workbook, filename);
};

export const __private__ = {
  normalizeBirthDate,
  normalizeTargetType,
  buildTemplateRow,
  deriveLidAantalFromStatus,
  reconcileConfirmedLeden,
  buildStableRowId,
  isValidEmail,
  getUploadsSubcollection
};